import { useState, useCallback, useEffect, useMemo } from "react";
import { Node, Edge } from "reactflow";
import { 
  GlobalVariableRegistry,
  DecisionVariablesRegistry,
  InterjectionGroup,
  FlowData
} from "../utils/types";
import {
  saveFlowToLocalStorage,
  hasUnsavedChanges as oldHasUnsavedChanges
} from "../utils/utils";

// Manual save hook
export const useManualSave = (
  nodes: Node[],
  edges: Edge[],
  globalVariableRegistry: GlobalVariableRegistry,
  decisionVariablesRegistry: DecisionVariablesRegistry,
  identifiers: string[],
  interjectionGroups: InterjectionGroup[]
) => {
  const manualSave = useCallback(() => {
    const savedFlow: FlowData = {
      version: "1.0",
      timestamp: Date.now().toString(),
      metadata: {
        status: "draft"
      },
      data: {
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        interjectionGroups,
        nodeIdCounter: nodes.length,
        isFirstNode: nodes.length === 0
      }
    };
    
    saveFlowToLocalStorage(savedFlow);
    
    // Manual save completed
  }, [nodes, edges, globalVariableRegistry, decisionVariablesRegistry, identifiers, interjectionGroups]);

  // Auto-save status hook
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved">("idle");

  // Auto-save to localStorage and check for unsaved changes whenever data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const hasChanges = oldHasUnsavedChanges(
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        interjectionGroups
      );

      // Check if state actually changed from initial state (more sensitive detection)
      const hasActualChanges = nodes.length > 0 || 
                               edges.length > 0 || 
                               Object.keys(globalVariableRegistry).length > 0 ||
                               Object.keys(decisionVariablesRegistry).length > 0 ||
                               identifiers.length > 0 ||
                               interjectionGroups.length > 0;

      if (hasActualChanges) {
        setAutoSaveStatus("saving");
        
        // Perform the save
        const savedFlow: FlowData = {
          version: "1.0",
          timestamp: Date.now().toString(),
          metadata: {
            status: "draft"
          },
          data: {
            nodes,
            edges,
            globalVariableRegistry,
            decisionVariablesRegistry,
            identifiers,
            interjectionGroups,
            nodeIdCounter: nodes.length,
            isFirstNode: nodes.length === 0
          }
        };
        
        saveFlowToLocalStorage(savedFlow);
        
        // Brief delay to show "saving" status
        setTimeout(() => {
          setAutoSaveStatus("saved");
          
          // Reset to idle after showing "saved" status
          setTimeout(() => {
            setAutoSaveStatus("idle");
          }, 1000);
        }, 200);
      }
    }, 1000); // 1 second delay for auto-save

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, globalVariableRegistry, decisionVariablesRegistry, identifiers, interjectionGroups]);

  // Additional properties that the main component expects
  const hasUnsavedChanges = useMemo(() => {
    return oldHasUnsavedChanges(
      nodes,
      edges,
      globalVariableRegistry,
      decisionVariablesRegistry,
      identifiers,
      interjectionGroups
    );
  }, [nodes, edges, globalVariableRegistry, decisionVariablesRegistry, identifiers, interjectionGroups]);

  const lastSavedTimestamp = useMemo(() => Date.now().toString(), []);

  return { 
    manualSave, 
    autoSaveStatus, 
    hasUnsavedChanges, 
    lastSavedTimestamp, 
    saveFlow: manualSave 
  };
};